import classNames from "classnames"
import ApplicationComponent from "legacy/ApplicationComponent"
import BoatSVG from "legacy/BoatSVG"
import _ from "lodash"
import PropTypes from "prop-types"
import React from "react"

/**
 * BoatSVGShape receives geometry data and a shape string that determines what type of svg shape object will be returned.
 * @param {number} centerX - represents the center point of the shape along the x axis, used for rotating along its center axis.
 * @param {number} centerY - represents the center point of the shape along the y axis, used for rotating along its center axis.
 * @param {method} clearPopover - method that clears the popover
 * @param {string} innerText - text to place over a shape being rendered.
 * @param {object} geometry - the geometry data that determines the size, location, and rotation of the shape.
 * @param {string} linearDockImage - url for dock image for annotating linear space rectangle.
 * @param {boolean} assignmentClicked - boolean of whether the current space has been clicked or not.
 * @param {method} onClick - method to run when the object is clicked.
 * @param {method} setPopover - method that triggers the popover to show
 * @param {string} shape - the type of shape that is going to be rendered, used to determine which css classes will be applied and what to do with the geometry data
 */

class BoatSVGShape extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {
      hover: false,
    }
    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.perimeterBoxHW = 12
  }

  /**
   * takes a coordinate param and returns an x or y coordinate that determines where the text will be displayed on the shape being rendered.
   * the values that are returned are the relative coordinates that the text will be placed on the x and y axis depending on which coordinate param is entered.
   * @param {string} coordinate
   * @returns {number} - an x or y coordinate
   */
  calculateTextPosition(coordinate, geometry, fontSize) {
    // places the text inside the boat image
    if (coordinate === "x") {
      if (
        geometry.rotation > 90 &&
        geometry.rotation < 270 &&
        geometry.rotation !== 90
      ) {
        return geometry.centerX - geometry.width / 1.7
      } else {
        return geometry.centerX - geometry.width / 3.25
      }
    } else {
      return geometry.centerY - fontSize
    }
  }

  calculateIconPosition(geometry) {
    if (this.isElementUpsideDown(geometry.rotation)) {
      return geometry.x + (geometry.width - 10)
    } else {
      return geometry.x + (geometry.width - 30)
    }
  }

  /**
   * takes a rotation param and returns the rotation of the text being displayed. This ensures that no matter the rotation of the shape, the text will always be upright.
   * @param {number} rotation
   * @returns {number} - the rotation of the text being displayed.
   */
  calculateTextRotation(rotation) {
    if (this.isElementUpsideDown(rotation)) {
      return 180
    } else {
      return 0
    }
  }

  /**
   * returns a number to shift the annotation rect based on the height and width of the perimeter boxes.
   * @returns {number} - the rotation of the text being displayed.
   */
  calculateAnnotationSVGShift() {
    return this.perimeterBoxHW / 2 + 2
  }

  /**
   * takes a geometry object param and returns a string to be inserted into the transform property of SVG elements.
   * @param {object} geometry
   * @returns {string} - a string that represents where the object should be located based on scale and rotation
   */
  getTransformString(geometry) {
    return `rotate(${geometry.rotation}, ${geometry.centerX}, ${geometry.centerY})`
  }

  /**
   * returns true if the shape props includes the 'assignment' string. This means that it is a shape that is clickable.
   * @returns {boolean}
   */
  isClickable() {
    return (
      this.props.shape.includes("assignment") ||
      this.props.shape.includes("set") ||
      this.props.shape.includes("placeholder")
    )
  }

  /**
   * returns tailwind class string for text color depending on type of assignment.
   * @returns {string}
   */
  spaceTextClass() {
    const shape = this.props.shape
    if (shape.includes("set") || shape.includes("empty")) {
      return "text-blue-900"
    } else if (
      shape === "checked-in-assignment" &&
      !this.props.reservationLabel
    ) {
      if (this.props.reservationLabelsEnabled) {
        return "text-gray-800"
      } else {
        return "text-white"
      }
    } else {
      return "text-gray-800"
    }
  }

  isElementUpsideDown(rotation) {
    return rotation > 90 && rotation < 270
  }

  spaceTextOrientation(rotation) {
    if (this.isElementUpsideDown(rotation)) {
      return "flex-row-reverse"
    } else {
      return "flex-row"
    }
  }

  /**
   * returns tailwind class string for boat SVG depending on shape type.
   * @returns {string}
   */
  boatSVGClass() {
    const shape = this.props.shape
    const reservationLabel = this.props.reservationLabel
    if (shape === "checked-in-assignment") {
      if (reservationLabel) {
        return `rl-bg-${reservationLabel.color}-dark ${
          this.state.hover || this.props.assignmentClicked
            ? "brightness-75"
            : null
        }`
      } else {
        if (this.props.reservationLabelsEnabled) {
          return this.state.hover || this.props.assignmentClicked
            ? "fill-blue-500"
            : "fill-blue-400"
        } else {
          return this.state.hover || this.props.assignmentClicked
            ? "fill-blue-800"
            : "fill-blue-600"
        }
      }
    } else if (shape.includes("conflicting")) {
      return this.state.hover || this.props.assignmentClicked
        ? "fill-yellow-600"
        : "fill-yellow-400"
    } else if (shape.includes("annotation") || shape.includes("set")) {
      return "fill-blue-50"
    } else if (shape === "placeholder") {
      return this.state.hover || this.props.assignmentClicked
        ? "fill-gray-400"
        : "fill-gray-200"
    } else {
      if (reservationLabel) {
        return `rl-bg-${reservationLabel.color}-light ${
          this.state.hover || this.props.assignmentClicked
            ? "brightness-75"
            : null
        }`
      } else {
        return this.state.hover || this.props.assignmentClicked
          ? "fill-blue-500"
          : "fill-blue-400"
      }
    }
  }

  /**
   * handles change of hover state when the mouse enters the current object and sets the popover.
   */
  handleMouseEnter() {
    Amplitude.trackEvent("assignments:hover_map_space")
    this.setState(
      {
        hover: true,
      },
      () => {
        this.props.setPopover()
      }
    )
  }

  /**
   * handles change of hover state when the mouse leaves the current object and clears the popover.
   */
  handleMouseLeave() {
    this.setState(
      {
        hover: false,
      },
      () => {
        this.props.clearPopover()
      }
    )
  }

  renderDockBoatSVG(geometry) {
    if (this.props.shape.includes("linear")) {
      return this.renderLinearSpaceBoatSVG(geometry)
    } else {
      return this.renderBoatSVG(geometry)
    }
  }

  renderSpaceRectangle(geometry, borderRadius) {
    return (
      <rect
        className={classNames({
          "annotation-rect fill-blue-900 stroke-blue-600 stroke-2":
            this.props.shape.includes("annotation"),
        })}
        x={geometry.x}
        y={geometry.y}
        width={geometry.width}
        height={geometry.height}
        rx={borderRadius}
      />
    )
  }

  renderSelectedBoat(geometry) {
    // Calculate the center of the expanded boat shape
    const expandedCenterX = geometry.x - 10 + (geometry.width + 20) / 2
    const expandedCenterY = geometry.y - 10 + (geometry.height + 20) / 2

    return (
      <BoatSVG
        x={geometry.x - 10}
        y={geometry.y - 10}
        width={geometry.width + 20}
        height={geometry.height + 20}
        className={classNames(
          { "cursor-pointer": this.isClickable() },
          this.boatSVGClass(),
          "animate-ping stroke-transparent opacity-75"
        )}
        style={{ transformOrigin: `${expandedCenterX}px ${expandedCenterY}px` }}
      />
    )
  }

  renderSpaceText(geometry) {
    const fontSize = _.min([geometry.height, geometry.width]) / 3
    if (this.props.innerText) {
      return (
        <foreignObject
          x={this.calculateTextPosition("x", geometry)}
          y={this.calculateTextPosition("y", geometry, fontSize * 0.7)}
          height="1"
          width="1"
          textAnchor="center"
          transform={`rotate(${this.calculateTextRotation(
            geometry.rotation
          )}, ${geometry.centerX}, ${geometry.centerY})`}
          className="overflow-visible"
        >
          <div
            onClick={this.props.onClick}
            className={classNames(
              "flex font-semibold",
              this.spaceTextClass(),
              this.spaceTextOrientation(geometry.rotation),
              {
                "cursor-pointer": this.isClickable(),
                "cursor-default": !this.isClickable(),
              }
            )}
            style={{
              width: `${geometry.width}px`,
              height: `${geometry.height * 0.65}px`,
              fontSize: `${fontSize}px`,
            }}
          >
            <span className="h-full w-7/12 truncate">
              {this.props.innerText}
            </span>
          </div>
        </foreignObject>
      )
    }
  }

  renderIcon(geometry) {
    const fontSize = _.min([geometry.height, geometry.width]) / 2.75
    return (
      <foreignObject
        x={this.calculateIconPosition(geometry)}
        y={parseInt(geometry.y) + parseInt(geometry.height * 0.25)}
        height="1"
        width="1"
        textAnchor="center"
        className="overflow-visible"
        transform={`rotate(${this.calculateTextRotation(
          geometry.rotation
        )}, ${this.calculateIconPosition(geometry)}, ${geometry.centerY})`}
      >
        <div
          onClick={this.props.onClick}
          className="-mt-0.5 cursor-pointer"
          style={{ fontSize: `${fontSize}px` }}
        >
          {this.props.shape === "checked-in-assignment" ? (
            <i
              className={classNames("icon icon-check text-white", {
                "text-gray-800": this.props.reservationLabelsEnabled,
              })}
            />
          ) : null}
          {this.props.shape === "placeholder" ? (
            <i
              className={classNames("icon icon-thumb-tack mt-1")}
              style={{ fontSize: `${fontSize}px` }}
            />
          ) : null}
          {this.props.shape.includes("conflicting") ? (
            <i
              className={classNames("icon icon-caution mt-2")}
              style={{ fontSize: `${fontSize}px` }}
            />
          ) : null}
        </div>
      </foreignObject>
    )
  }

  renderBoatSVG(geometry) {
    return (
      <BoatSVG
        x={geometry.x}
        y={geometry.y}
        height={geometry.height > 0 ? geometry.height : 0}
        width={geometry.width}
        className={classNames(
          { "cursor-pointer": this.isClickable() },
          this.boatSVGClass(),
          "stroke-transparent"
        )}
        onClick={this.props.onClick}
      />
    )
  }

  renderPerimeterBoxes(geometry) {
    return (
      <g className="fill-white">
        <rect
          className="annotation-rect"
          x={geometry.x - this.perimeterBoxHW / 2}
          y={geometry.y - this.perimeterBoxHW / 2}
          height={this.perimeterBoxHW}
          width={this.perimeterBoxHW}
          rx="1"
        />
        <rect
          className="annotation-rect"
          x={geometry.x - this.perimeterBoxHW / 2}
          y={geometry.y - this.perimeterBoxHW / 2 + geometry.height}
          height={this.perimeterBoxHW}
          width={this.perimeterBoxHW}
          rx="1"
        />
        <rect
          className="annotation-rect"
          x={geometry.x - this.perimeterBoxHW / 2 + geometry.width}
          y={geometry.y - this.perimeterBoxHW / 2}
          height={this.perimeterBoxHW}
          width={this.perimeterBoxHW}
          rx="1"
        />
        <rect
          className="annotation-rect"
          x={geometry.x - this.perimeterBoxHW / 2 + geometry.width}
          y={geometry.y - this.perimeterBoxHW / 2 + geometry.height}
          height={this.perimeterBoxHW}
          width={this.perimeterBoxHW}
          rx="1"
        />
      </g>
    )
  }

  renderLinearSpaceBoatSVG(geometry) {
    return (
      <g className={classNames("stroke-transparent", this.boatSVGClass())}>
        <BoatSVG
          x={geometry.x}
          y={geometry.y}
          height={geometry.height > 0 ? geometry.height : 0}
          width={geometry.width * 0.33}
        />
        <BoatSVG
          x={geometry.x + geometry.width * 0.33}
          y={geometry.y}
          height={geometry.height > 0 ? geometry.height : 0}
          width={geometry.width * 0.33}
        />
        <BoatSVG
          x={geometry.x + geometry.width * 0.66}
          y={geometry.y}
          height={geometry.height > 0 ? geometry.height : 0}
          width={geometry.width * 0.34}
        />
        <BoatSVG
          x={geometry.x + geometry.width * 0.66}
          y={geometry.y}
          height={geometry.height > 0 ? geometry.height : 0}
          width={geometry.width * 0.34}
        />
        <image
          className={classNames({
            "annotation-rect": this.props.shape.includes("annotation"),
          })}
          preserveAspectRatio="none"
          href={this.props.linearDockImage}
          x={geometry.x}
          y={geometry.y - 5}
          height={geometry.height > 0 ? 10 : 0}
          width={geometry.width}
        />
      </g>
    )
  }

  /**
   * takes the shape and geometry props and returns an svg shape object based on those props.
   * @returns {object} - svg shape based on props
   */
  drawShape() {
    const shape = this.props.shape || "empty-rect"
    const geometry = {
      x: this.props.geometry.x ? parseInt(this.props.geometry.x) : 0,
      y: this.props.geometry.y ? parseInt(this.props.geometry.y) : 0,
      width: this.props.geometry.width
        ? parseInt(this.props.geometry.width)
        : 0,
      height: this.props.geometry.height
        ? parseInt(this.props.geometry.height)
        : 0,
      rotation: this.props.geometry.rotation
        ? parseInt(this.props.geometry.rotation)
        : 0,
      centerX: this.props.centerX ? parseInt(this.props.centerX) : 0,
      centerY: this.props.centerY ? parseInt(this.props.centerY) : 0,
    }
    switch (shape) {
      case "annotation-rect":
      case "linear-annotation-rect":
        return (
          <g
            className="cursor-move fill-blue-900 stroke-blue-600 stroke-2"
            transform={this.getTransformString(geometry)}
          >
            <svg
              id="annotation-rect"
              x={geometry.x - this.calculateAnnotationSVGShift()}
              y={geometry.y - this.calculateAnnotationSVGShift()}
              height={geometry.height * 2}
              width={geometry.width * 2}
              viewBox={`${geometry.x - this.calculateAnnotationSVGShift()}, ${
                geometry.y - this.calculateAnnotationSVGShift()
              }, ${geometry.width * 2}, ${geometry.height * 2}`}
            >
              {this.renderSpaceRectangle(geometry)}
              {this.renderDockBoatSVG(geometry)}
              {/* Corner Boxes */}
              {geometry.height > 0 &&
                geometry.width > 0 &&
                this.renderPerimeterBoxes(geometry)}
            </svg>
          </g>
        )
      case "map-set-rect":
      case "linear-space-set-rect":
        return (
          <g
            className="cursor-pointer fill-blue-300 hover:fill-blue-500"
            onClick={this.props.onClick}
            transform={this.getTransformString(geometry)}
          >
            {this.renderSpaceRectangle(geometry, 3)}
            {this.renderDockBoatSVG(geometry)}
            {this.renderSpaceText(geometry)}
          </g>
        )
      case "empty-space":
      case "empty-linear-space":
        return (
          <g
            className="fill-transparent"
            transform={this.getTransformString(geometry)}
          >
            {this.renderSpaceRectangle(geometry)}
            {this.renderSpaceText(geometry)}
          </g>
        )
      case "checked-in-assignment":
      case "confirmed-assignment":
      case "conflicting-assignments":
      case "conflicting-with-placeholder":
      case "placeholder":
        return (
          <g
            transform={this.getTransformString(geometry)}
            onMouseEnter={this.handleMouseEnter}
            onMouseLeave={this.handleMouseLeave}
          >
            {this.props.assignmentClicked
              ? this.renderSelectedBoat(geometry)
              : null}
            {this.renderDockBoatSVG(geometry)}
            {this.renderSpaceText(geometry)}
            {this.renderIcon(geometry)}
          </g>
        )
      case "bulk-action-rect":
        return (
          <g
            className="fill-transparent stroke-blue-600 stroke-2"
            transform={this.getTransformString(geometry)}
            style={{ strokeDasharray: "5" }}
          >
            <rect
              id="bulk-action-rect"
              x={parseInt(geometry.x)}
              y={parseInt(geometry.y)}
              height={geometry.height}
              width={geometry.width}
            />
          </g>
        )
    }
  }

  render() {
    return this.drawShape()
  }
}

BoatSVGShape.propTypes = {
  centerX: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  centerY: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  clearPopover: PropTypes.func,
  innerText: PropTypes.string,
  geometry: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    rotation: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  linearDockImage: PropTypes.string,
  assignmentClicked: PropTypes.bool,
  onClick: PropTypes.func,
  setPopover: PropTypes.func,
  shape: PropTypes.string,
  reservationLabel: PropTypes.shape({
    color: PropTypes.string,
  }),
  reservationLabelsEnabled: PropTypes.bool,
}

export default BoatSVGShape
